import { createAuthClient } from 'better-auth/vue';
import {
  convexClient,
  crossDomainClient,
} from '@convex-dev/better-auth/client/plugins';
import { passkeyClient } from '@better-auth/passkey/client';
import { oneTapClient } from 'better-auth/client/plugins';
import { useRuntimeConfig } from 'nuxt/app';
import { createLogger } from '../../utils/logger';

const log = createLogger('AuthClient');

export interface AuthClientOptions {
  includePasskey?: boolean;
  includeOneTap?: boolean;
  sessionOptions?: { refetchOnWindowFocus?: boolean; refetchInterval?: number };
}

function buildAuthClient(options: AuthClientOptions = {}) {
  const { includePasskey = true, includeOneTap = true, sessionOptions } = options;
  const config = useRuntimeConfig();
  const baseURL = config.public.convexSiteUrl as string;

  if (!baseURL && import.meta.dev) {
    log.error('NUXT_PUBLIC_CONVEX_SITE_URL not set');
  }

  // Build the plugin list using spread so TypeScript can infer the full union type.
  // This lets createAuthClient expose all plugin methods (e.g. oneTap, passkey) on the returned client.
  return createAuthClient({
    baseURL,
    sessionOptions: {
      // Disable focus-triggered session refetches — these cause frequent /get-session
      // calls every time a tab or Capacitor WebView regains focus, producing noisy
      // "Client disconnected" logs in Convex with no meaningful benefit.
      refetchOnWindowFocus: false,
      // Allow per-project overrides to layer on top of the default above.
      ...sessionOptions,
    },
    plugins: [
      convexClient(),
      crossDomainClient(),
      ...(includePasskey ? [passkeyClient()] : []),
      ...(includeOneTap ? [oneTapClient({
        clientId: config.public.googleClientId as string,
        autoSelect: true,
        cancelOnTapOutside: true,
        context: 'signin',
        promptOptions: {
          baseDelay: 1000,
          maxAttempts: 5,
        },
      })] : []),
    ],
  });
}

// Singleton auth client instances (one per options combo)
let defaultInstance: ReturnType<typeof buildAuthClient> | null = null;

/**
 * Auth client for Better Auth with Convex integration.
 * Provides authentication methods for email/password, social (Google, Apple), and passkeys.
 *
 * This is a singleton - the same client instance is returned on each call.
 */
export const useAuthClient = () => {
  if (defaultInstance) {
    return defaultInstance;
  }

  defaultInstance = buildAuthClient();
  return defaultInstance;
};

/**
 * Create a custom auth client composable with specific options.
 * Used by projects that need different plugin configurations.
 */
export function createAuthClientComposable(options: AuthClientOptions) {
  let instance: ReturnType<typeof buildAuthClient> | null = null;
  return () => {
    if (instance) return instance;
    instance = buildAuthClient(options);
    return instance;
  };
}
